import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDispatch, useAppDispatch, useAppSelector } from '../../../../store/store';
import { Cycle } from '../../../../store/models/cycles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../../../constants/route-constants';
import { useAbilityContext } from '../../../../components/Can/Can';
import { Action } from '../../../../enums/action';
import { EXTENDED_INTERVAL_UPDATE_DATA, PAGE_TITLE } from '../../../../constants/common';
import { IActionsDropdown, IActionsTag } from '../../../../types/tableDropdown';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../../types/element-size';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import IconCopy from '../../../../components/Icons/IconCopy/IconCopy';
import IconCheck from '../../../../components/Icons/IconCheck/IconCheck';
import CycleStatus from '../../../../enums/cycle-status.enum';
import { filterCyclesBySearchQuery } from '../../utils/filterCyclesBySearchQuery';
import { usePolling } from '../../../../hooks/usePolling';
import { useTranslation } from 'react-i18next';
import { OpenReport } from '../../../../components/Icons/OpenReport/OpenReport';
import { Paperclip } from '../../../../components/Icons/Paperclip/Paperclip';
import IconScreen from '../../../../components/Icons/IconScreen/IconScreen';
import { Screen } from '../../../../store/models/screens';
import IconCycle from '../../../../components/Icons/IconCycle/IconCycle';
import IconSwitcher from '../../../../components/Icons/IconSwitcher/IconSwitcher';
import { joinActionGroups } from '../../../../utils/actionsDropdown';

export interface ICyclesScreenInteractorProps {
  screenId: string;
  onDeleteScreen: () => Promise<void>;
}

export interface ICyclesScreenInteractor {
  cycles: Cycle[];
  cyclesBySearch: Cycle[];

  isLoading: boolean;
  canOpenSearchModal: boolean;
  canOpenDeleteModal: boolean;

  screenPath: string | null;
  editCyclePath: string | null;
  canShowCriticalDeviation: boolean;
  contractId: string;
  clientId: string;

  cycleActions: IActionsDropdown[];

  addCycleToScreen: (cycleId: string) => Promise<void>;
  refetchCycleSource: (sourceScreenId: string | null) => Promise<void>;
  getCyclesByContractId: AppDispatch['cycles']['getAllByContractId'];
  deleteScreenHandler: () => Promise<void>;
  updateCriticalDeviation: (CriticalDeviation: number) => Promise<void>;

  cycleIdSettings: string;
  cycleNameSettings: string;
  openSelectedCycleSettings: (cycle: Cycle) => void;
  closeSelectedCycleSettings: () => void;

  cycleIdDelete: string;
  openSelectedCycleDelete: (cycleId: string) => void;
  closeSelectedCycleDelete: () => void;
  onAcceptSelectedCycleDelete: () => void;

  cycleIdComplete: string;
  openSelectedCycleComplete: (cycleId: string) => void;
  closeSelectedCycleComplete: () => void;
  onAcceptSelectedCycleComplete: () => void;

  cycleIdMakeInactive: string;
  openSelectedCycleMakeInactive: (cycleId: string) => void;
  closeSelectedCycleMakeInactive: () => void;
  onAcceptSelectedCycleMakeInactive: () => void;

  cycleIdAttach: string;
  isSaveAttachment: boolean;
  closeSelectedCycleAttach: () => void;
  onAcceptSelectedCycleAttach: () => void;
  onSelectFile: (file: File | null) => void;

  cycleIdChangeScreen: string;
  closeSelectedCycleChangeScreen: () => void;
  onChangeScreenToCycle: (screenId: string, oldScreenId?: string) => Promise<void>;

  screensBySearch: Screen[];
  getScreensByContractId: AppDispatch['screens']['getAllByContractId'];

  cycleIdDuplicate: string;
  cycleNameDuplicate: string;
  closeSelectedCycleDuplicate: () => void;
  onCycleDuplicated: () => void;
}

export const useCyclesScreenInteractor = ({
  screenId,
  onDeleteScreen,
}: ICyclesScreenInteractorProps): ICyclesScreenInteractor => {
  const [isLoading, setIsLoading] = useState(false);
  // const [cycles, setCycles] = useState<Cycle[]>([]);
  const [cycleIdSettings, setCycleIdSettings] = useState<string>('');
  const [cycleNameSettings, setCycleNameSettings] = useState<string>('');
  const [cycleIdDelete, setCycleIdDelete] = useState<string>('');
  const [cycleIdComplete, setCycleIdComplete] = useState<string>('');
  const [cycleIdMakeInactive, setCycleIdMakeInactive] = useState<string>('');
  const [cycleIdAttach, setCycleIdAttach] = useState<string>('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSaveAttachment, setIsSaveAttachment] = useState<boolean>(false);
  const [cycleIdChangeScreen, setCycleIdChangeScreen] = useState<string>('');
  const [cycleIdDuplicate, setCycleIdDuplicate] = useState<string>('');
  const [cycleNameDuplicate, setCycleNameDuplicate] = useState<string>('');

  const { clientId, contractId } = useParams();
  const navigate = useNavigate();

  const {
    cycles: {
      updateCycle,
      getAllByContractId,
      deleteScreenFromCycle,
      getScreenCycles,
      getScreenlessCycles,
      getAttachments,
      resetAttachments,
      saveAttachment,
    },
    screens: { delete: deleteScreen, update: updateScreen, getAllByContractId: getScreensByContractId },
  } = useAppDispatch();

  const { cycles: cyclesBySearch, activeScreensCycles, searchCycleQuery } = useAppSelector((state) => state.cycles);
  const { screens: screensBySearch } = useAppSelector((state) => state.screens);

  const ability = useAbilityContext();
  const { t } = useTranslation();

  const [canDeleteScreen, canUpdateScreen] = useMemo(() => {
    return [
      ability.can(Action.Delete, PAGE_TITLE.CREATE_SCREEN),
      ability.can(Action.Update, PAGE_TITLE.EDIT_SCREEN),
    ];
  }, [ability]);

  const [
    canDeleteCycle,
    canUpdateCycle,
    canCompleteCycle,
    canUpdateCycleReport,
  ] = useMemo(() => {
    return [
      ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Update, PAGE_TITLE.CYCLE_REPORT),
    ];
  }, [ability]);

  const isChangeLoading = useRef(true);

  const fetchDataHandler = async (screenId: string) => {
    try {
      if (!screenId) {
        return;
      }

      if (isChangeLoading.current) {
        setIsLoading(true);
      }

      await getScreenCycles(screenId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isChangeLoading.current = false;
    }
  };

  const selectCurrentScreenCycles = useCallback(
    (screenId: string): Cycle[] => {
      if (!screen || !activeScreensCycles || !(screenId in activeScreensCycles)) {
        return [];
      }

      const currentScreenCycles = activeScreensCycles[screenId];

      return currentScreenCycles;
    },
    [activeScreensCycles],
  );

  const getCurrentScreenCycles = useCallback(() => {
    const currentCycles = selectCurrentScreenCycles(screenId);

    if (searchCycleQuery) {
      return filterCyclesBySearchQuery(currentCycles, searchCycleQuery);
    }

    return currentCycles;
  }, [selectCurrentScreenCycles, screenId, searchCycleQuery]);

  const cycles = useMemo(() => {
    return getCurrentScreenCycles();
  }, [getCurrentScreenCycles]);

  //TODO CLEAR SCREEN CYCLES STORE??
  const deleteScreenHandler = useCallback(async () => {
    await deleteScreen(screenId);
    await onDeleteScreen();
    await getScreenlessCycles(contractId);
  }, [screenId, contractId]);

  //attach to cycle
  const openSelectedCycleAttach = useCallback((cycleId: string) => {
    setCycleIdAttach(cycleId);
  }, []);

  const closeSelectedCycleAttach = useCallback(() => {
    setCycleIdAttach('');
  }, []);

  //cycle settings
  const openSelectedCycleSettings = useCallback((cycle: Cycle) => {
    setCycleIdSettings(cycle.id);
    setCycleNameSettings(cycle.name);
  }, []);

  const closeSelectedCycleSettings = useCallback(() => {
    setCycleIdSettings('');
    setCycleNameSettings('');
  }, []);

  //cycle delete
  const openSelectedCycleDelete = useCallback((cycleId: string) => {
    setCycleIdDelete(cycleId);
  }, []);

  const closeSelectedCycleDelete = useCallback(() => {
    setCycleIdDelete('');
  }, []);

  const dataRefetch = async (screenId: string, contractId: string) => {
    await Promise.all([getScreenlessCycles(contractId), fetchDataHandler(screenId)]);
  };

  const onAcceptSelectedCycleDelete = useCallback(async () => {
    closeSelectedCycleDelete();
    await deleteScreenFromCycle({ screenId, cycleId: cycleIdDelete });

    await dataRefetch(screenId, contractId);
  }, [screenId, cycleIdDelete, contractId, closeSelectedCycleDelete]);

  //cycle complete
  const openSelectedCycleComplete = useCallback((cycleId: string) => {
    setCycleIdComplete(cycleId);
  }, []);

  const closeSelectedCycleComplete = useCallback(() => {
    setCycleIdComplete('');
  }, []);

  const onAcceptSelectedCycleComplete = useCallback(async () => {
    closeSelectedCycleComplete();
    await updateCycle({ id: cycleIdComplete, status: CycleStatus.Completed });

    await dataRefetch(screenId, contractId);
  }, [screenId, cycleIdComplete, contractId, closeSelectedCycleComplete]);

  //cycle make inactive
  const openSelectedCycleMakeInactive = useCallback((cycleId: string) => {
    setCycleIdMakeInactive(cycleId);
  }, []);

  const closeSelectedCycleMakeInactive = useCallback(() => {
    setCycleIdMakeInactive('');
  }, []);

  const onAcceptSelectedCycleMakeInactive = useCallback(async () => {
    closeSelectedCycleMakeInactive();
    await updateCycle({ id: cycleIdMakeInactive, status: CycleStatus.InActive });

    await dataRefetch(screenId, contractId);
  }, [screenId, cycleIdMakeInactive, contractId, closeSelectedCycleMakeInactive]);

  const addCycleToScreen = useCallback(
    async (cycleId: string) => {
      await updateCycle({ id: cycleId, screenId: screenId });

      await dataRefetch(screenId, contractId);
    },
    [screenId, contractId],
  );

  // After a drag-and-drop move, the screen the cycle came FROM also needs its
  // cycle list refreshed - otherwise it keeps showing its stale local copy
  // until its next poll, which looks like the cycle was duplicated.
  const refetchCycleSource = useCallback(
    async (sourceScreenId: string | null) => {
      if (sourceScreenId) {
        await getScreenCycles(sourceScreenId);
      } else {
        await getScreenlessCycles(contractId);
      }
    },
    [contractId],
  );

  // Matches the behaviour of the edit pencil on the cycle table (Cycle.tsx/CycleV2.tsx):
  // opens Edit Cycle for the screen's cycle, instead of Edit Screen.
  const editCyclePath = useMemo(() => {
    if (!clientId || !contractId || !screenId || !canUpdateCycle || !cycles.length) {
      return null;
    }

    return generatePath(ROUTE.EDIT_CYCLE, { clientId, contractId, screenId, id: cycles[0].id });
  }, [clientId, contractId, screenId, canUpdateCycle, cycles]);

  const canShowCriticalDeviation = !!clientId && !!contractId && !!screenId && canUpdateScreen;

  const screenPath = useMemo(() => {
    if (!clientId || !contractId || !screenId) {
      return null;
    }
    return generatePath(ROUTE.SCREEN_CYCLES, { clientId, contractId, screenId });
  }, [clientId, contractId, screenId]);

  const onAcceptSelectedCycleAttach = useCallback(async () => {
    try {
      setIsSaveAttachment(true);
      await saveAttachment({ id: cycleIdAttach, attachment: attachment });
    } finally {
      setIsSaveAttachment(false);
    }
  }, [cycleIdAttach, attachment]);

  const onSelectFile = (file: File | null) => {
    setAttachment(file);
  };

  //change screen
  const openSelectedCycleChangeScreen = useCallback((cycleId: string) => {
    setCycleIdChangeScreen(cycleId);
  }, []);

  const closeSelectedCycleChangeScreen = useCallback(() => {
    setCycleIdChangeScreen('');
  }, []);

  const onChangeScreenToCycle = useCallback(
    async (newScreenId: string, oldScreenId?: string) => {
      closeSelectedCycleChangeScreen();
      const payload: any = { id: cycleIdChangeScreen, screenId: newScreenId };
      if (oldScreenId) {
        payload.oldScreenId = oldScreenId;
      }
      await updateCycle(payload);

      await dataRefetch(screenId, contractId);
    },
    [cycleIdChangeScreen, screenId, contractId],
  );

  //multi-duplicate cycle
  const openSelectedCycleDuplicate = useCallback((cycleId: string, cycleName: string) => {
    setCycleIdDuplicate(cycleId);
    setCycleNameDuplicate(cycleName);
  }, []);

  const closeSelectedCycleDuplicate = useCallback(() => {
    setCycleIdDuplicate('');
    setCycleNameDuplicate('');
  }, []);

  const onCycleDuplicated = useCallback(() => {
    dataRefetch(screenId, contractId);
  }, [screenId, contractId]);

  const openCycleReportActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <OpenReport stroke="#000" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.openReport'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, { id: cycleId, clientId, contractId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const openCycleChartActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCycle fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.openChart'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE, { clientId, contractId, screenId, id: cycleId });
        window.open(`${path}?bucket=6`, '_blank', 'noopener,noreferrer');
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [clientId, contractId, screenId]);

  const attachToCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <Paperclip stroke="#000" size={ElementSize.XXS} />,
      name: t('commonText.viewAttachments'),
      onClick: (cycleId: string) => {
        openSelectedCycleAttach(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleAttach]);

  const editCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.edit'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.EDIT_CYCLE, { clientId, contractId, screenId: screenId, id: cycleId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [screenId, navigate, clientId, contractId]);

  const deleteCycleFromScreenActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconBin fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.removeFromScreen'),
      onClick: (cycleId: string) => {
        openSelectedCycleDelete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleDelete]);

  const completeCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCheck fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.complete'),
      onClick: (cycleId: string) => {
        openSelectedCycleComplete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleComplete]);

  const makeInactiveCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => (
        <div className="flex h-4 items-center justify-center text-warning">
          <IconSwitcher size={ElementSize.XXS} />
        </div>
      ),
      name: t('commonText.makeInactive'),
      onClick: (cycleId: string) => {
        openSelectedCycleMakeInactive(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleMakeInactive]);

  const changeScreenActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconScreen fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.changeScreen'),
      onClick: (cycleId: string) => {
        openSelectedCycleChangeScreen(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleChangeScreen]);

  const duplicateCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCopy fill="#000" size={ElementSize.XXS} />,
      name: 'Multi-duplicate',
      onClick: (cycleId: string, cycleData: Cycle) => {
        openSelectedCycleDuplicate(cycleId, cycleData.name);
      },
      loading: false,
    };
  }, [openSelectedCycleDuplicate]);

  const cycleActions: IActionsDropdown[] = useMemo(() => {
    const viewGroup: IActionsDropdown[] = [
      openCycleChartActionBtn,
      ...(canUpdateCycle ? [attachToCycleActionBtn] : []),
      ...(canUpdateCycleReport ? [openCycleReportActionBtn] : []),
    ];

    const screenGroup: IActionsDropdown[] = [
      ...(canUpdateCycle ? [changeScreenActionBtn] : []),
      ...(canDeleteCycle ? [deleteCycleFromScreenActionBtn] : []),
    ];

    const editGroup: IActionsDropdown[] = canUpdateCycle ? [editCycleActionBtn, duplicateCycleActionBtn] : [];

    const statusGroup: IActionsDropdown[] = [
      ...(canCompleteCycle ? [completeCycleActionBtn] : []),
      ...(canCompleteCycle ? [makeInactiveCycleActionBtn] : []),
    ];

    return joinActionGroups(viewGroup, screenGroup, editGroup, statusGroup);
  }, [
    canUpdateCycle,
    canCompleteCycle,
    canDeleteCycle,
    canUpdateCycleReport,
    editCycleActionBtn,
    deleteCycleFromScreenActionBtn,
    changeScreenActionBtn,
    openCycleChartActionBtn,
    openCycleReportActionBtn,
    duplicateCycleActionBtn,
    attachToCycleActionBtn,
    completeCycleActionBtn,
    makeInactiveCycleActionBtn,
  ]);

  usePolling(() => fetchDataHandler(screenId), EXTENDED_INTERVAL_UPDATE_DATA, [screenId]);

  const updateCriticalDeviation = useCallback(
    async (critical_deviation: number) => {
      await updateScreen({ screenId, payload: { critical_deviation } });

      await dataRefetch(screenId, contractId);
    },
    [screenId],
  );

  useEffect(() => {
    if (cycleIdAttach) {
      getAttachments(cycleIdAttach);
    }

    return () => {
      resetAttachments();
    };
  }, [cycleIdAttach]);

  return {
    cycles,
    cyclesBySearch,
    isLoading,
    screenPath,
    editCyclePath,
    canShowCriticalDeviation,
    cycleActions,

    canOpenSearchModal: canUpdateCycle,
    canOpenDeleteModal: canDeleteScreen,
    contractId,
    clientId,
    addCycleToScreen,
    refetchCycleSource,
    deleteScreenHandler,
    getCyclesByContractId: getAllByContractId,
    updateCriticalDeviation,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    openSelectedCycleDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //cycle complete
    cycleIdComplete,
    openSelectedCycleComplete,
    closeSelectedCycleComplete,
    onAcceptSelectedCycleComplete,

    //cycle make inactive
    cycleIdMakeInactive,
    openSelectedCycleMakeInactive,
    closeSelectedCycleMakeInactive,
    onAcceptSelectedCycleMakeInactive,

    //attach to cycle
    cycleIdAttach,
    isSaveAttachment,
    closeSelectedCycleAttach,
    onAcceptSelectedCycleAttach,
    onSelectFile,

    //change screen
    cycleIdChangeScreen,
    closeSelectedCycleChangeScreen,
    onChangeScreenToCycle,

    screensBySearch,
    getScreensByContractId,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  };
};
